/**
 * Occupational Therapy Discharge Note — options + empty payload.
 * After shared common header (1–6): Reason → Subjective → Status table →
 * Functional → Cognition → UE ROM → Pain → Goals → Interventions → HEP →
 * Education → Discharge Plan → Communication → Narrative
 * (per OT Discharge Summary screenshots).
 */

import { emptyOtCommonHeader } from "./otNotesCommonHeaderUtils";

/** Row-major order so a 2-column grid matches the screenshot columns. */
export const DISCHARGE_REASON_OPTIONS = [
  "Goals Met",
  "Maximum Functional Potential Achieved",
  "Patient Independent with ADLs",
  "Patient Declined Further OT",
  "Patient Hospitalized",
  "Patient Transferred to Facility",
  "Physician Discontinued Therapy",
  "Insurance Authorization Ended",
  "Non-Compliance",
  "Medical Status Changed",
];

export const STATUS_METRICS = [
  "ADL Performance",
  "Fine Motor Skills",
  "Cognition/Perception",
];

export const ASSIST_LEVEL_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Supervision",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
  "Not Assessed",
];

export const FINE_MOTOR_STATUS_OPTIONS = [
  "Within Normal Limits",
  "Impaired",
  "Functional",
  "Not Assessed",
];

export const COGNITION_STATUS_OPTIONS = [
  "Intact",
  "Impaired",
  "Not Assessed",
];

export const STATUS_DISCHARGE_OPTIONS = {
  "ADL Performance": ASSIST_LEVEL_OPTIONS,
  "Fine Motor Skills": FINE_MOTOR_STATUS_OPTIONS,
  "Cognition/Perception": COGNITION_STATUS_OPTIONS,
};

/** Row-major pairing matches the 2-column screenshot. */
export const FUNCTIONAL_TASKS = [
  { key: "bathingLowerBody", label: "Bathing (Lower Body)" },
  { key: "feedingEating", label: "Feeding/Eating" },
  { key: "dressingUpperBody", label: "Dressing (Upper Body)" },
  { key: "toiletingHygiene", label: "Toileting Hygiene" },
  { key: "groomingHygiene", label: "Grooming/Hygiene" },
  { key: "toiletTransfers", label: "Toilet Transfers" },
];

export const ORIENTATION_OPTIONS = ["Oriented x4", "Impaired"];

export const PROBLEM_SOLVING_OPTIONS = [
  "Functional",
  "Impaired",
  "Unable",
  "Not Assessed",
];

export const VISUAL_PERCEPTUAL_OPTIONS = [
  "Within Normal Limits",
  "Impaired",
  "Not Assessed",
];

export const AFFECTED_SIDE_OPTIONS = ["Left", "Right", "Bilateral"];

export const UE_JOINT_ROWS = [
  "Shoulder Flexion",
  "Elbow Extension",
  "Hand Grip (lbs)",
];

export const PAIN_FREQUENCY_OPTIONS = [
  "None",
  "Rare",
  "Occasional",
  "Intermittent",
  "Constant",
];

export const OT_GOAL_ROWS = [
  "ADL Independence",
  "Safety Awareness",
  "Transfer Goals",
  "UE Function Goals",
  "Energy Conservation Goals",
];

export const GOAL_STATUS_OPTIONS = ["Met", "Partially Met", "Not Met"];

/** Row-major order so a 2-column grid matches the screenshot columns. */
export const OT_INTERVENTION_OPTIONS = [
  "ADL Retraining (Bathing, Dressing)",
  "IADL Retraining (Meal Prep, Med Mgmt)",
  "Therapeutic Exercise (UE Strength/ROM)",
  "Cognitive Retraining / Memory Compensation",
  "Energy Conservation Techniques",
  "Safety Education & Fall Prevention",
];

export const HEP_FLAG_OPTIONS = [
  "HEP Provided/Updated",
  "Patient Demonstrates Independence",
];

/** Row-major order so a 2-column grid matches the screenshot columns. */
export const EDUCATION_TOPIC_OPTIONS = [
  "Joint Protection / Ergonomics",
  "Assistive Technology / DME Use",
  "Home Modification Recommendations",
  "Task Simplification / Pacing",
];

export const FOLLOW_UP_OPTIONS = [
  "Discharge to Home (No further OT)",
  "Home with Caregiver Support",
  "Outpatient OT Recommended",
  "Skilled Nursing Facility",
  "Hospital",
  "Other",
];

export const OT_DISCHARGE_ARRAY_KEYS = [
  "dischargeReasons",
  "interventionsCompleted",
  "hepFlags",
  "educationTopics",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const today = () => new Date().toISOString().slice(0, 10);

export const emptyStatusComparisonRow = (metric) => ({
  metric,
  admissionStatus: "",
  dischargeStatus: "",
});

export const emptyStatusComparisonRows = () =>
  STATUS_METRICS.map((metric) => emptyStatusComparisonRow(metric));

export const emptyFunctionalAssessment = () =>
  Object.fromEntries(FUNCTIONAL_TASKS.map((task) => [task.key, ""]));

export const emptyUeRomRow = (joint) => ({
  joint,
  rom: "",
  mmt: "",
  notes: "",
});

export const emptyUeRomRows = () => UE_JOINT_ROWS.map((joint) => emptyUeRomRow(joint));

export const emptyGoalStatuses = () =>
  Object.fromEntries(OT_GOAL_ROWS.map((goal) => [goal, ""]));

export const mergeStatusComparisonRows = (loaded) => {
  const byMetric = {};
  (Array.isArray(loaded) ? loaded : []).forEach((row) => {
    const key = row?.metric || row?.parameter;
    if (STATUS_METRICS.includes(key)) byMetric[key] = row;
  });
  return STATUS_METRICS.map((metric) => ({
    metric,
    admissionStatus: String(
      byMetric[metric]?.admissionStatus ||
        byMetric[metric]?.initialBaseline ||
        "",
    ),
    dischargeStatus: String(
      byMetric[metric]?.dischargeStatus ||
        byMetric[metric]?.dischargeCurrent ||
        "",
    ),
  }));
};

export const mergeUeRomRows = (loaded) => {
  const byJoint = {};
  (Array.isArray(loaded) ? loaded : []).forEach((row) => {
    if (row?.joint) byJoint[row.joint] = row;
  });
  return UE_JOINT_ROWS.map((joint) => ({
    joint,
    rom: String(byJoint[joint]?.rom || ""),
    mmt: String(byJoint[joint]?.mmt || ""),
    notes: String(byJoint[joint]?.notes || ""),
  }));
};

export const mergeFunctionalAssessment = (loaded) => {
  const base = emptyFunctionalAssessment();
  const src = loaded && typeof loaded === "object" && !Array.isArray(loaded)
    ? loaded
    : {};
  FUNCTIONAL_TASKS.forEach(({ key }) => {
    base[key] = String(src[key] || "");
  });
  return base;
};

export const mergeGoalStatuses = (loaded) => {
  const base = emptyGoalStatuses();
  const src = loaded && typeof loaded === "object" && !Array.isArray(loaded)
    ? loaded
    : {};
  OT_GOAL_ROWS.forEach((goal) => {
    const value = String(src[goal] || "");
    base[goal] = GOAL_STATUS_OPTIONS.includes(value) ? value : "";
  });
  return base;
};

/** Full editable OT Discharge Note payload (JSON on dedicated row). */
export const emptyOtDischargePayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  dischargeDate: "",
  socDate: "",
  commonHeader: emptyOtCommonHeader(),

  // —— 7. Reason for OT Discharge ——
  dischargeReasons: [],
  dischargeReasonOther: "",

  // —— 8. Subjective Report ——
  subjectiveReport: "",

  // —— 9. Patient Status at Discharge ——
  statusComparison: emptyStatusComparisonRows(),

  // —— 10. Functional Assessment ——
  functionalAssessment: emptyFunctionalAssessment(),

  // —— 11. Cognition & Perception ——
  orientation: "",
  problemSolving: "",
  visualPerceptual: "",

  // —— 12. Upper Extremity ROM & Strength ——
  affectedSide: "",
  ueRomStrength: emptyUeRomRows(),

  // —— 13. Pain Assessment ——
  painLevel: null,
  painFrequency: "",

  // —— 14. OT Goals Status ——
  goalStatuses: emptyGoalStatuses(),
  goalComments: "",

  // —— 15. Skilled OT Interventions ——
  interventionsCompleted: [],

  // —— 16. Home Exercise Program ——
  hepFlags: [],
  exerciseFrequency: "",

  // —— 17. Education Provided ——
  educationTopics: [],

  // —— 18. Discharge Plan ——
  followUpRecommendation: "",

  // —— 19. Physician / Care Team Communication ——
  communicationNotes: "",

  // —— 20. OT Discharge Summary Narrative ——
  dischargeSummary: "",
});
